import { doc, updateDoc, serverTimestamp, getDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { auditLogger } from "../../services/auditLogger";

export type ApprovalStatus = "approved" | "rejected";

export interface ApprovalUpdateResult {
  success: boolean;
  error?: string;
}

export async function updateApprovalStatus(
  id: string,
  status: ApprovalStatus,
  user?: string,
  comment?: string
): Promise<ApprovalUpdateResult> {
  try {
    // Fetch the current expense data for audit trail
    const expenseRef = doc(db, "expenses", id);
    const expenseSnap = await getDoc(expenseRef);
    
    if (!expenseSnap.exists()) {
      return {
        success: false,
        error: "Expense not found"
      };
    }

    const beforeData = expenseSnap.data();

    // Update the expense
    const updatePayload = {
      approvalStatus: status,
      approvedBy: user || "",
      approvalDate: serverTimestamp(),
      updatedBy: user || "",
      updatedAt: serverTimestamp(),
      ...(comment ? { approvalComment: comment } : {})
    };

    await updateDoc(expenseRef, updatePayload);

    // Prepare after data for audit log
    const afterData = {
      ...beforeData,
      ...updatePayload,
      approvalDate: new Date(),
      updatedAt: new Date()
    };

    // Log to audit trail
    if (status === "approved") {
      await auditLogger.logExpenseApproval(
        id,
        beforeData,
        afterData,
        {
          customMetadata: {
            approvedBy: user,
            approvalComment: comment,
            approvalSource: 'updateApprovalStatus'
          }
        }
      );
    } else {
      await auditLogger.logExpenseRejection(
        id,
        beforeData,
        afterData,
        comment,
        {
          customMetadata: {
            rejectedBy: user,
            approvalSource: 'updateApprovalStatus'
          }
        }
      );
    }

    return { success: true };
  } catch (err) {
    console.error("Failed to update approval status", err);
    return {
      success: false,
      error: err instanceof Error ? err.message : "Unknown error",
    };
  }
}
